import { Heart } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { ProductCardResponse } from "@/shared/api/generated/iSHONCHPlatformAPI.schemas";
import { cn } from "@/shared/lib/cn";
import { haptic } from "@/shared/platform";
import { useToggleFavorite } from "../model/useFavorites";

/**
 * The heart on a product card and in the product top bar: grey outline, filled red when saved (D14), a short pop
 * when added. 40 px touch target. Works for guests too (kept in this browser until login).
 */
export function FavoriteButton({
  product,
  isFavorite,
  floating = false,
}: {
  product: ProductCardResponse;
  isFavorite: boolean;
  /** White circle over a photo (product gallery). */
  floating?: boolean;
}) {
  const { t } = useTranslation();
  const toggle = useToggleFavorite();

  return (
    <button
      type="button"
      className={cn(
        "flex size-10 items-center justify-center rounded-full",
        floating ? "size-11 bg-surface/90 shadow-sm" : "active:bg-canvas",
      )}
      aria-label={t("product.favorite")}
      aria-pressed={isFavorite}
      onClick={(event) => {
        // the heart lives on a card whose link covers it
        event.preventDefault();
        event.stopPropagation();
        if (!isFavorite) haptic("tap");
        toggle(product, isFavorite);
      }}
    >
      <Heart
        key={String(isFavorite)}
        aria-hidden
        className={cn(
          "size-[22px] stroke-[1.75]",
          isFavorite ? "animate-pop fill-danger text-danger" : "text-control",
        )}
      />
    </button>
  );
}
